import React, { createContext, useState, useContext, useEffect } from "react";
import { authAPI } from "../services/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const response = await authAPI.getCurrentUser();
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        console.error("Auth check failed:", error);
        localStorage.removeItem("token");
        setIsAuthenticated(false);
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const {
        access_token,
        user_id,
        username,
        email: userEmail,
        full_name,
        role,
      } = response.data;

      localStorage.setItem("token", access_token);
      setUser({
        id: user_id,
        username,
        email: userEmail,
        full_name,
        role,
      });
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      console.error("Login failed:", error);
      return {
        success: false,
        error: error.response?.data?.detail || "Login failed",
      };
    }
  };

  // Establish a session from a backend JWT obtained out-of-band (Okta SSO callback).
  const loginWithToken = async (token) => {
    try {
      localStorage.setItem("token", token);
      const response = await authAPI.getCurrentUser();
      setUser(response.data);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      console.error("SSO token exchange failed:", error);
      localStorage.removeItem("token");
      setIsAuthenticated(false);
      return { success: false, error: "Could not establish session" };
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to refresh user:", error);
      return null;
    }
  };

  const logout = () => {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("vulnhub_")) {
        localStorage.removeItem(key);
      }
    });
    localStorage.removeItem("token");
    setUser(null);
    setIsAuthenticated(false);
  };

  const role = user?.role || null;
  // Internal role values stay pentester/blue_team/wasabian. Display labels
  // (Red Teamer / Blue Teamer / Wasabian) live in ROLE_LABELS (Sidebar).
  const isPentester = role === "pentester"; // "Red Teamer" - full access
  const isBlueTeam = role === "blue_team"; // "Blue Teamer" - monitoring plane only
  const isWasabian = role === "wasabian"; // read-only viewer

  // Capability flags (mirror the backend gates):
  //  - isOperator: can ACT on the monitoring plane (monitor/subdomain/leaks
  //    writes, beacon ops). Red + Blue Teamer.
  //  - canViewOffensive: may VIEW the offensive plane (Pentests, Status).
  //    Red Teamer + Wasabian; Blue Teamer is walled off.
  const isOperator = isPentester || isBlueTeam;
  const canViewOffensive = isPentester || isWasabian;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        login,
        loginWithToken,
        logout,
        refreshUser,
        role,
        isPentester,
        isBlueTeam,
        isWasabian,
        isOperator,
        canViewOffensive,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
